import { useContext, useEffect, useState } from "react";
import { getGelTokens } from "@tal-gel/theming";
import {
  GelScreenDetectorContext,
  GelRowLayout,
  GelContainerLite,
  GelTabs,
  GelTab,
  GelHeading2,
  GelParagraph,
  GelSpinner
} from "@tal-gel/components";
import { UserContext } from "../../common/usercontext/user.context";
import { useNavigate } from "react-router-dom";
import ClaimBenefit from "./policy/claimBenefit";
import {
  ACTIVITY_TYPE,
  CLAIM_ASSESSMENT_STATUS,
  CLAIM_STATUS_TYPE,
  NotificationType,
} from "../../constants/constants";
import Card from "../../common/components/card";
import styled from "@emotion/styled";
import { GET_MEMBER_NOTIFICATIONS_QUERY } from "../../graphql/queries/graphql-queries.constant";
import { FormatHelper } from "../../common/formatHelper.common";
import ClaimHistory from "./history/claimHistory";
import { useCookies } from "react-cookie";
import OtherDocuments from "./otherDocuments/otherdocuments";
import ResponsiveContainer from "../../components/responsive.container";
import { AdobeAnalytics } from "../../common/analytics/adobe-analytics";
import { useGetClaimDetailsContent } from "../../common/api/graphQLDataFetch";
import NotificationCard from "./notifications/notification-card";
import { MemberNotification } from "../../common/models/memberclaims";
import InfoAlert from "../../common/components/InfoAlert";
import { activeUploadActions, getActiveUploadState } from "./documentsupload/activeUploadStore";
import { useUnmount } from "../../common/hooks/useUnmount";
import { useGelTabsOnChange } from '../../common/hooks/useGelTabsOnChange';
import { distinctByBenefitGroupId, formatAsCurrency, getNextScheduledPayment } from "../payments/helpers";
import { interpolateString } from "../../common/helpers/helpers";
import MmcGraphQL from "../../common/api/mmc-graphql";

const { global: { sizeBaseHalf, sizeNone, sizeBaseX20, sizeBaseX3, sizeBaseX15, sizeBaseX5, sizeBaseX10, themeColorBackgroundLight, sizeBaseUnit } } = getGelTokens();

const TabBorderOverride = styled.div(() => ({
  margin: -sizeBaseUnit / 4,
  border: sizeNone
}));

const ClaimDetailsContainer = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetClaimDetailsContent()
  const [{ tpid }] = useCookies(["tpid"]);

  const { screen, isXsScreen } = useContext(
    GelScreenDetectorContext
  ) as GelScreenDetectorContext;

  const { contextData, setUserContext } = useContext(UserContext);
  const [notificationDataLoaded, setnotificationDataLoaded] = useState(false);
  const [claimCookie] = useCookies(["selectedClaim"]);
  const [ notifications, setNotifications ] = useState<MemberNotification[]>([]);
  
  const PageHeader = styled.div(() => ({
    padding: isXsScreen(screen)
      ? `${sizeBaseX15}px ${sizeBaseX5
      }px`
      : `${sizeBaseX15}px ${sizeBaseX10
      }px`,
    backgroundColor: themeColorBackgroundLight,
  }));

  if (contextData.memberDetails?.displayLegalDisclosure)
    navigate("/yourDeclaration");

  useEffect(() => {
    AdobeAnalytics.PushFormStepEvent(tpid)
    AdobeAnalytics.PushPageViewEvent(tpid)

    // [CAMMC-665] Ensure we only fetch notifications once member request is complete, Probably a better way to do this
    if (contextData?.memberDetails?.memberNumber) {
      loadNotifications();
    }
  }, [contextData?.memberDetails?.memberNumber]);

  // TODO: refactor this function
  const loadNotifications = async () => {
    let { list} = await MmcGraphQL<{ list: MemberNotification[] }>(GET_MEMBER_NOTIFICATIONS_QUERY)
    
    setNotifications(list);
    addNotificationsToContext(list);

    setnotificationDataLoaded(true);

    setTimeout(() => {
      const { activeRequirementId } = getActiveUploadState();
      if (!activeRequirementId) return;

      const el = document.getElementById(String(activeRequirementId));
      el?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  const addNotificationsToContext = (notifications: MemberNotification[]) => {
    removeProcessedNotificationFromContext();
    notifications?.forEach((nt) => {
      let claim = contextData.claims?.find(
        (claim) => claim.claimId === nt.claimId
      );

      if (
        claim &&
        claim.requirements.filter(({ id }) => id === nt.notificationId).length === 0
      ) {
        claim?.requirements.push({
          description: nt.text,
          friendlyText: nt.title!,
          type: NotificationType.Activity,
          id: nt.notificationId,
          claimId: nt.claimId,
          requestedDate: nt.createdOn,
          completedDate: nt.readOn!,
        });
      }
    });

    // Scheduled payments notifications
    contextData.claims?.forEach((claim) => {
      let nextScheduledPayment = getNextScheduledPayment(claim.claimId, contextData?.scheduledPayments);
      if (nextScheduledPayment) {
        let notificationExists = claim.requirements.find(n => 
          n.type === NotificationType.FuturePayment && 
          n.id === nextScheduledPayment.scheduledPaymentId);

        if (!notificationExists)
          claim.requirements.push({
            description: new Date(nextScheduledPayment.paymentDate).toLocaleDateString('en-AU'),
            friendlyText: nextScheduledPayment.amount.toString(),
            type: NotificationType.FuturePayment,
            id: nextScheduledPayment.scheduledPaymentId,
            claimId: claim.claimId,
            requestedDate: nextScheduledPayment.paymentDate,
          });
      }
    });

    // Historical payments notifications
    distinctByBenefitGroupId(contextData?.historicalPayments ?? [])
    .filter(hp => !hp.isRead)
    .forEach((hp) => {
      let claim = contextData.claims?.find(c => c.claimId === hp.claimId);
      if (claim) {
        let notificationExists = claim.requirements.find(n => n.type === NotificationType.ProceesedPayment && n.id === hp.paymentId);
        if (!notificationExists) {
          let message = interpolateString(
            data?.payment?.PaymentMessage!,
            formatAsCurrency(hp.totalPaymentAmount, false),
            new Date(hp.paymentDate).toLocaleDateString('en-AU')
          );
          claim.requirements.push({
            description: message,
            friendlyText: hp.totalPaymentAmount.toString(),
            id: hp.paymentId,
            type: NotificationType.ProceesedPayment,
            claimId: claim.claimId,
            requestedDate: hp.paymentDate,
          });
        }
      }
    });

    contextData.claims?.forEach((claim) => {
      claim.activities.forEach((ac) => (ac.hideActivity = false));
    });

    notifications?.forEach((nt) => {
      let claim = contextData.claims?.find(
        (claim) => claim.claimId === nt.claimId
      );
      if (claim) {

        claim.activities
          .filter((at) => at.claimPaymentId === nt.sourceId)
          .map((x) => (x.hideActivity = true));

        if (nt.type === NotificationType.Activity) {
          claim.activities
            .filter((at) => at.type === ACTIVITY_TYPE.ACTIVITY)
            .filter(at => {
              // exclude these exact titles…
              const excluded = [
                "Claim Closed",
                "Claim Started",
                "Claim Re-opened",
                "Claim Approved"
              ];
              return !excluded.includes(at.title)
                && !at.title.startsWith(CLAIM_STATUS_TYPE.RTS);
            })
            .map((x) => (x.hideActivity = true));
        }
      }
    });

    contextData.claims?.forEach((claim) => {
      claim.requirements.sort(
        (a, b) =>
          FormatHelper.toDate(b.requestedDate).getTime() -
          FormatHelper.toDate(a.requestedDate).getTime()
      );
    });
    setUserContext(contextData);
  };

  const removeProcessedNotificationFromContext = () => {
    contextData.claims?.forEach((claim) => {
      claim.requirements
        .filter((rq) => rq.type === NotificationType.Activity)
        .forEach((ppay) => {
          claim.requirements.splice(claim.requirements.indexOf(ppay), 1);
        });
    });
  };

  const { selectedClaim } = claimCookie;
  if (selectedClaim) {
    const claims = contextData.claims ?? [];
    const index = claims.findIndex(c => c.caseNumber === String(selectedClaim));

    if (index > -1) {
      const [claim] = claims.splice(index, 1);
      contextData.claims = [claim, ...claims];
    }
  }

  // Prevent stale “active requirement” from leaking: clear on page unmount and on any tab switch.
  useUnmount(() => activeUploadActions.clearActiveRequirement());
  const tabsHandlers = useGelTabsOnChange(() => activeUploadActions.clearActiveRequirement());

  if (isLoading) return <GelSpinner medium overlay />;

  return (
    <GelContainerLite
      style={{ padding: sizeNone, overflow: "hidden" }}
    >
      <PageHeader>
        <GelHeading2>
          {data?.headerText}
        </GelHeading2>

        <GelParagraph style={{ paddingTop: sizeBaseX3 }}>
          {data?.headerTextDescription}
        </GelParagraph>
      </PageHeader>


      <GelTabs {...tabsHandlers} style={{ border: sizeNone, marginRight: -sizeBaseHalf }}>
        {contextData.claims?.map((claim, index) => (
          <GelTab
            key={claim.claimId}
            title={claim.claimTypeName}
            tabindex={index}
            removeContentPadding
          >
            <TabBorderOverride>
              <ClaimBenefit claimBenefit={claim} />
              
              <ResponsiveContainer>
                <GelRowLayout
                  style={{
                    padding: isXsScreen(screen)
                      ? `${sizeBaseX10}px ${sizeBaseX5
                      }px`
                      : `${sizeBaseX5}px ${sizeBaseX10
                      }px`,
                  }}
                >

                  {claim?.status?.description?.includes(CLAIM_STATUS_TYPE.RTS) && (
                    <InfoAlert
                      title={''}
                      body= {data?.claimStatusMessageReferredToSuperFund.json.content[0].content[0].value}
                      style={{ marginTop: '15px' }}/>
                  )}
                  {claim?.status?.description === CLAIM_STATUS_TYPE.CL && (
                    <InfoAlert
                      title={''}
                      body={"This claim is closed. If you think this is incorrect or have any questions about your claim, you can get help by selecting 'Need help?' at the top of this page."}
                      style={{ marginTop: '15px' }}/>
                  )}

                  {(claim?.claimAssesmentStatus !== CLAIM_ASSESSMENT_STATUS.RN 
                    && claim?.status?.description !== CLAIM_STATUS_TYPE.CL) && (
                    <NotificationCard 
                      requirements={claim.requirements}
                      notifications={notifications}
                      isNotificationDataLoaded={notificationDataLoaded}
                    />
                  )}

                  {(claim?.status?.description !== CLAIM_STATUS_TYPE.CL
                    && !claim?.status?.description?.includes(CLAIM_STATUS_TYPE.RTS)) && (
                    <Card
                      key={`claim-${claim.claimId}-other-documents`}
                      title={data?.additionalDocuments?.OtherDocumentsCardHeader!}
                      lightHeader={true}
                      subTitle={data?.additionalDocuments?.OtherDocumentsCardHeaderDescription}
                      showFooter={false}
                    >
                      {notificationDataLoaded && (
                        <OtherDocuments
                          claimId={claim.claimId}
                          claimAssesmentStatus={claim.claimAssesmentStatus}
                        />
                      )}
                    </Card>
                  )}

                  <Card
                    key={`claim-${claim.claimId}-history`}
                    title={data?.claimHistory?.ClaimsHistoryHeader!}
                    lightHeader={true}
                    subTitle={data?.claimHistory?.ClaimsHistoryHeaderDescription}
                    showFooter={false}
                  >
                    {!notificationDataLoaded && (
                      <div style={{ height: sizeBaseX20 }}>
                        <GelSpinner medium overlay />
                      </div>
                    )}

                    {notificationDataLoaded && (
                      <ClaimHistory
                        claimDetails={claim}
                      />
                    )}
                  </Card>

                </GelRowLayout>
              </ResponsiveContainer>
            </TabBorderOverride>
          </GelTab>
        ))}
      </GelTabs>

    </GelContainerLite>
  );
};

export default ClaimDetailsContainer;
